import { Box, Button, Checkbox, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { Close, KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import type { Hierarchy } from "@/api/flash.service";

interface Props {
  open: boolean;
  onClose: () => void;
  lojas: Hierarchy[];
  categories: Hierarchy[];
  showCatFilter: boolean;
  selectedLojas: Set<string>;
  setSelectedLojas: (s: Set<string>) => void;
  selectedCats: Set<string>;
  setSelectedCats: (s: Set<string>) => void;
  expandedFilterCats: Set<string>;
  setExpandedFilterCats: (s: Set<string>) => void;
  catSubData: Record<string, Hierarchy[]>;
  loadingCatSubRows: Record<string, boolean>;
  fetchSubCats: (code: string) => void;
  resolveCompanyName: (cod: string, fallback: string) => string;
}

export function FilterDrawer({
  open, onClose, lojas, categories, showCatFilter,
  selectedLojas, setSelectedLojas, selectedCats, setSelectedCats,
  expandedFilterCats, setExpandedFilterCats, catSubData, loadingCatSubRows,
  fetchSubCats, resolveCompanyName,
}: Props) {
  const getAllDescendantCodes = (code: string): string[] => {
    const children = (catSubData[code] ?? []).filter((c) => c.nomeempresaapp);
    return [...children.map((c) => c.cod_empresa), ...children.flatMap((c) => getAllDescendantCodes(c.cod_empresa))];
  };

  const renderCatFilterItem = (cat: Hierarchy, depth = 0, ancestors: string[] = []) => {
    const active = selectedCats.has(cat.cod_empresa);
    const isExpanded = expandedFilterCats.has(cat.cod_empresa);
    const isLoading = loadingCatSubRows[cat.cod_empresa] ?? false;
    const children = (catSubData[cat.cod_empresa] ?? []).filter((c) => c.nomeempresaapp);
    const someChildSelected = children.some((c) => selectedCats.has(c.cod_empresa));
    const someChildDeselected = children.some((c) => !selectedCats.has(c.cod_empresa));
    const isIndeterminate = children.length > 0 && someChildSelected && someChildDeselected;
    const canExpand = cat.canalapp !== "PRODUTO";

    const toggleExpand = () => {
      const next = new Set(expandedFilterCats);
      if (next.has(cat.cod_empresa)) next.delete(cat.cod_empresa);
      else { next.add(cat.cod_empresa); fetchSubCats(cat.cod_empresa); }
      setExpandedFilterCats(next);
    };

    const toggleSelect = () => {
      const next = new Set(selectedCats);
      const descendants = getAllDescendantCodes(cat.cod_empresa);
      if (active) {
        next.delete(cat.cod_empresa);
        descendants.forEach((code) => next.delete(code));
        for (let i = ancestors.length - 1; i >= 0; i--) {
          const ancestorCode = ancestors[i];
          if (!ancestorCode) break;
          const siblings = (catSubData[ancestorCode] ?? []).filter((c) => c.nomeempresaapp);
          if (siblings.length > 0 && siblings.every((s) => !next.has(s.cod_empresa))) next.delete(ancestorCode);
          else break;
        }
      } else {
        next.add(cat.cod_empresa);
        descendants.forEach((code) => next.add(code));
        for (let i = ancestors.length - 1; i >= 0; i--) {
          const ancestorCode = ancestors[i];
          if (!ancestorCode) break;
          const siblings = (catSubData[ancestorCode] ?? []).filter((c) => c.nomeempresaapp);
          if (siblings.length > 0 && siblings.every((s) => next.has(s.cod_empresa))) next.add(ancestorCode);
          else break;
        }
      }
      setSelectedCats(next);
    };

    return (
      <Box key={cat.cod_empresa}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ pl: 2 + depth * 2, py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
          {canExpand ? (
            <IconButton size="small" onClick={toggleExpand}>
              {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 34 }} />
          )}
          <Checkbox
            size="small"
            checked={active}
            indeterminate={isIndeterminate}
            onChange={toggleSelect}
            disabled={isLoading}
          />
          <Typography variant="body2" sx={{ flex: 1 }}>{cat.nomeempresaapp}</Typography>
        </Stack>
        {isExpanded && !isLoading && children.map((child) => renderCatFilterItem(child, depth + 1, [...ancestors, cat.cod_empresa]))}
      </Box>
    );
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: 320 } } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" fontWeight={700}>Filtros</Typography>
        <IconButton onClick={onClose}><Close /></IconButton>
      </Stack>

      <Box sx={{ overflowY: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">LOJAS</Typography>
          <Stack direction="row" gap={1.5}>
            <Button size="small" onClick={() => setSelectedLojas(new Set(lojas.map((l) => l.cod_empresa)))}>Todos</Button>
            <Button size="small" color="error" onClick={() => setSelectedLojas(new Set())}>Limpar</Button>
          </Stack>
        </Stack>
        {[...lojas]
          .sort((a, b) => resolveCompanyName(a.cod_empresa, a.nomeempresaapp).localeCompare(resolveCompanyName(b.cod_empresa, b.nomeempresaapp), "pt-BR"))
          .map((l) => {
            const active = selectedLojas.has(l.cod_empresa);
            return (
              <Stack
                key={l.cod_empresa}
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ px: 2, py: 0.5, borderBottom: "1px solid", borderColor: "divider", cursor: "pointer" }}
                onClick={() => {
                  const next = new Set(selectedLojas);
                  active ? next.delete(l.cod_empresa) : next.add(l.cod_empresa);
                  setSelectedLojas(next);
                }}
              >
                <Checkbox size="small" checked={active} />
                <Typography variant="body2">{resolveCompanyName(l.cod_empresa, l.nomeempresaapp)}</Typography>
              </Stack>
            );
          })}

        {showCatFilter && (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, mt: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">CATEGORIAS</Typography>
              <Stack direction="row" gap={1.5}>
                <Button
                  size="small"
                  onClick={() => {
                    const all = new Set([
                      ...categories.map((c) => c.cod_empresa),
                      ...categories.flatMap((c) => getAllDescendantCodes(c.cod_empresa)),
                    ]);
                    setSelectedCats(all);
                  }}
                >
                  Todos
                </Button>
                <Button size="small" color="error" onClick={() => setSelectedCats(new Set())}>Limpar</Button>
              </Stack>
            </Stack>
            {categories.map((c) => renderCatFilterItem(c, 0))}
          </>
        )}
      </Box>
    </Drawer>
  );
}
